'use client';

/**
 * SkeletonGroup Component
 *
 * A composition utility that renders N copies of a skeleton element
 * with staggered animation delays for a cascading loading effect.
 *
 * Usage:
 * ```tsx
 * <SkeletonGroup count={4} stagger={80}>
 *   <Skeleton variant="rectangular" height={40} />
 * </SkeletonGroup>
 * ```
 */

import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import type React from 'react';
import { cloneElement, isValidElement, type ReactElement } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface SkeletonGroupProps {
  /** Number of skeleton copies to render */
  count: number;

  /** Delay between each copy in ms (default 80) */
  stagger?: number;

  /** Flex direction (default "column") */
  direction?: 'row' | 'column';

  /** Gap in theme spacing units (default 2) */
  spacing?: number;

  /** Single skeleton element to repeat */
  children: ReactElement;

  /** Additional styles */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Component
// ============================================================================

const SkeletonGroup = ({
  count,
  stagger = 80,
  direction = 'column',
  spacing = 2,
  children,
  sx,
}: SkeletonGroupProps) => {
  if (!isValidElement(children)) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: direction,
        gap: spacing,
        ...sx,
      }}
    >
      {Array.from({ length: Math.max(0, Math.floor(count)) }).map((_, index) => {
        const childProps = children.props as Record<string, unknown>;
        const existingStyle = (childProps['style'] as React.CSSProperties | undefined) ?? {};
        return cloneElement(children as React.ReactElement<Record<string, unknown>>, {
          key: index,
          style: {
            ...existingStyle,
            animationDelay: `${index * stagger}ms`,
          },
        });
      })}
    </Box>
  );
};

SkeletonGroup.displayName = 'SkeletonGroup';

export { SkeletonGroup };
export default SkeletonGroup;
